interface CreationOperation {
  payload: string;
  id: string;
}

// One unfinished creation per user and API, surviving a reload in the same tab.
// A different payload is a new creation; an ambiguous failure keeps its operation.
export const createSessionOperations = (getStorage: () => Storage | undefined) => {
  const pending = new Map<string, CreationOperation | undefined>();
  const read = (key: string): CreationOperation | undefined => {
    if (pending.has(key)) return pending.get(key);
    try {
      const saved = JSON.parse(getStorage()?.getItem(key) || 'null') as CreationOperation | null;
      if (
        saved &&
        typeof saved.payload === 'string' &&
        typeof saved.id === 'string' &&
        /^[A-Za-z0-9_-]{8,128}$/.test(saved.id)
      ) {
        return saved;
      }
    } catch {
      // Storage may be unavailable. Retries still work during this page lifetime.
    }
    return pending.get(key);
  };

  return {
    get(key: string, payload: string): string {
      const existing = read(key);
      if (existing?.payload === payload) return existing.id;
      const operation = {
        payload,
        id: Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
          byte.toString(16).padStart(2, '0'),
        ).join(''),
      };
      pending.set(key, operation);
      try {
        getStorage()?.setItem(key, JSON.stringify(operation));
      } catch {
        // Keep the in-memory operation when storage is disabled or full.
      }
      return operation.id;
    },
    complete(key: string, id: string): void {
      if (read(key)?.id !== id) return;
      pending.set(key, undefined);
      try {
        getStorage()?.removeItem(key);
      } catch {
        // The room is already created; storage cleanup must not fail the request.
      }
    },
  };
};
